
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import type { GeneratedProblem, GenerationJob, BaseProblem, NavSelection, Worksheet, Folder } from '../types';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';

// SVG Icons as React Components
const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
);

const FolderIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    </svg>
);

const FolderOpenIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
        <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm2 2v1h12V8H4z" clipRule="evenodd" />
    </svg>
);


const JobsIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379-1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>
);
const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>
);
const WorksheetIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
);
const ArrowIcon: React.FC<{ open: boolean, className?: string }> = ({ open, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`${className} transition-transform duration-200 ${open ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
);
const CollapseIcon: React.FC<{ className?: string, isCollapsed: boolean }> = ({ className, isCollapsed }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        {isCollapsed 
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
        }
    </svg>
);


interface NavigationPaneProps {
  style: React.CSSProperties;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  baseProblems: BaseProblem[];
  problemHistory: GeneratedProblem[];
  generationJobs: GenerationJob[];
  trashedProblems: (BaseProblem | GeneratedProblem)[];
  worksheets: Worksheet[];
  allFolderPaths: string[];
  currentSelection: NavSelection | null;
  onSelect: (selection: NavSelection) => void;
  onUpdateWorksheet: (worksheetId: string, data: Partial<Omit<Worksheet, 'id'>>) => void;
  onDeleteWorksheet: (worksheetId: string) => void;
  onRenameFolder: (oldPath: string, newPath: string) => Promise<void>;
  onDeleteFolder: (folderPath: string) => Promise<void>;
  newContentFolders: Set<string>;
}

const buildFolderTree = (paths: string[], problems: (BaseProblem | GeneratedProblem)[]): Folder[] => {
    const root: Folder = { name: 'root', path: '/', children: [], problemCount: 0 };
    const nodeMap = new Map<string, Folder>([['/', root]]);

    const allPathsWithParents = new Set<string>(paths);
    paths.forEach(path => {
      const parts = path.split('/');
      for (let i = 1; i < parts.length; i++) {
        allPathsWithParents.add(parts.slice(0, i).join('/'));
      }
    });

    const sortedPaths = Array.from(allPathsWithParents).sort();

    for (const path of sortedPaths) {
        if (!nodeMap.has(path)) {
            const name = path.includes('/') ? path.split('/').pop()! : path;
            const newNode: Folder = { name, path, children: [], problemCount: 0 };
            
            const parentPath = path.includes('/') ? path.substring(0, path.lastIndexOf('/')) : '/';
            const parentNode = nodeMap.get(parentPath);
            
            if (parentNode) {
                parentNode.children.push(newNode);
            } else {
                root.children.push(newNode);
            }
            nodeMap.set(path, newNode);
        }
    }

    problems.forEach(p => {
        const folderPath = p.folder || '/';
        if (nodeMap.has(folderPath)) {
            nodeMap.get(folderPath)!.problemCount += 1;
        }
    });

    nodeMap.forEach(node => {
        node.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return root.children;
};

const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div 
            className="relative group"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {children}
            {isVisible && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-2 py-1 bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] text-xs rounded-md shadow-lg whitespace-nowrap z-50 border border-[var(--color-border)]">
                    {text}
                </div>
            )}
        </div>
    );
};

const TreeItem: React.FC<{
  isCollapsed: boolean;
  path: string;
  label: string;
  count?: number;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  level: number;
  isBold?: boolean;
  hasChildren?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent) => void;
  hasNotification?: boolean;
}> = ({ isCollapsed, path, label, count, icon, isSelected, onClick, onContextMenu, level, isBold, hasChildren, isExpanded, onToggleExpand, hasNotification }) => {
  const content = (
    <div
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`w-full flex items-center text-left px-2 py-1.5 rounded-md transition-all duration-300 text-sm cursor-pointer group relative border border-transparent ${
        isSelected
          ? 'bg-[var(--color-accent)] text-[var(--color-accent-text)]'
          : `text-[var(--color-text-secondary)] hover:bg-white/10`
      } ${isBold ? 'font-bold' : 'font-medium'} ${hasNotification ? 'animate-border-glow' : ''}`}
      style={{ paddingLeft: isCollapsed ? '8px' : `${level * 16 + 8}px` }}
    >
      <div className={`flex-shrink-0 flex items-center ${isCollapsed ? 'justify-center w-full' : 'mr-2'}`}>
        {!isCollapsed && hasChildren ? (
          <div onClick={onToggleExpand} className="p-1 -ml-1 mr-1 rounded-full hover:bg-white/20">
            <ArrowIcon open={isExpanded!} className="h-4 w-4" />
          </div>
        ) : !isCollapsed ? (
          <div className="w-5" />
        ) : null}
        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            {icon}
        </div>
      </div>
      {!isCollapsed && <span className="flex-grow truncate">{label}</span>}
      {!isCollapsed && typeof count === 'number' && <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${isSelected ? 'bg-white/20' : 'bg-black/20'}`}>{count}</span>}
    </div>
  );

  return isCollapsed ? <Tooltip text={label}>{content}</Tooltip> : content;
};

const FolderTreeView: React.FC<{
    isCollapsed: boolean;
    nodes: Folder[];
    level: number;
    currentSelection: NavSelection | null;
    onSelect: (selection: NavSelection) => void;
    onContextMenu: (e: React.MouseEvent, path: string) => void;
    expandedPaths: Set<string>;
    toggleExpand: (path: string) => void;
    newContentFolders: Set<string>;
}> = ({ isCollapsed, nodes, level, currentSelection, onSelect, onContextMenu, expandedPaths, toggleExpand, newContentFolders }) => {
    return (
        <div className="mt-1 space-y-1">
            {nodes.map(node => {
                const isSelected = currentSelection?.type === 'folder' && currentSelection.id === node.path;
                const isExpanded = expandedPaths.has(node.path);
                
                const hasDirectNotification = newContentFolders.has(node.path);
                const hasDescendantNotification = Array.from(newContentFolders).some((folderPath) => (folderPath as string).startsWith(node.path + '/'));
                
                const hasNotification = hasDirectNotification || (!isExpanded && hasDescendantNotification);
                
                return (
                    <React.Fragment key={node.path}>
                        <TreeItem
                            isCollapsed={isCollapsed}
                            path={node.path}
                            label={node.name}
                            icon={isExpanded && node.children.length > 0 
                                ? <FolderOpenIcon className="h-full w-full" /> 
                                : <FolderIcon className="h-full w-full" />
                            }
                            count={node.problemCount}
                            isSelected={isSelected}
                            onClick={() => onSelect({ type: 'folder', id: node.path })}
                            onContextMenu={(e) => onContextMenu(e, node.path)}
                            level={level}
                            hasChildren={node.children.length > 0}
                            isExpanded={isExpanded}
                            onToggleExpand={(e) => { e.stopPropagation(); toggleExpand(node.path); }}
                            hasNotification={hasNotification}
                        />
                        {!isCollapsed && isExpanded && node.children.length > 0 && (
                            <FolderTreeView
                                isCollapsed={isCollapsed}
                                nodes={node.children}
                                level={level + 1}
                                currentSelection={currentSelection}
                                onSelect={onSelect}
                                onContextMenu={onContextMenu}
                                expandedPaths={expandedPaths}
                                toggleExpand={toggleExpand}
                                newContentFolders={newContentFolders}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};


const NavigationPane: React.FC<NavigationPaneProps> = ({
  style, isCollapsed, onToggleCollapse, baseProblems, problemHistory, generationJobs, trashedProblems, worksheets, allFolderPaths, currentSelection, onSelect, onUpdateWorksheet, onDeleteWorksheet, onRenameFolder, onDeleteFolder, newContentFolders
}) => {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['folder-root', 'worksheets']));
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, type: 'folder' | 'worksheet', id: string} | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dialog = useDialog();

  const folderTree = useMemo(() => buildFolderTree(allFolderPaths, [...baseProblems, ...problemHistory]), [allFolderPaths, baseProblems, problemHistory]);

  const activeJobsCount = useMemo(() => generationJobs.filter(j => j.status === 'pending' || j.status === 'processing').length, [generationJobs]);

  const toggleExpand = useCallback((key: string) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  }, []);

  useEffect(() => {
    if (currentSelection?.type === 'folder' && !isCollapsed) {
        const path = currentSelection.id;
        const parts = path.split('/');
        setExpandedPaths(prev => {
            const newSet = new Set(prev);
            newSet.add('folder-root');
            for (let i = 1; i < parts.length; i++) {
                newSet.add(parts.slice(0, i).join('/'));
            }
            return newSet;
        });
    }
  }, [currentSelection, isCollapsed]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleContextMenu = (e: React.MouseEvent, type: 'folder' | 'worksheet', id: string) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ x: e.clientX, y: e.clientY, type, id });
  };
  
  const handleRename = async () => {
    if (!contextMenu) return;
    const { type, id } = contextMenu;
    setContextMenu(null);
    
    if (type === 'worksheet') {
        const ws = worksheets.find(w => w.id === id);
        if (!ws) return;
        const newName = await dialog.prompt('새 학습지 이름을 입력하세요:', { defaultValue: ws.name });
        if (newName && newName.trim() !== '') {
            onUpdateWorksheet(ws.id, { name: newName.trim() });
        }
    } else { // folder
        const oldName = id.split('/').pop() || id;
        const newName = await dialog.prompt('새 폴더 이름을 입력하세요:', { defaultValue: oldName });
        if (newName && newName.trim() !== '' && newName.trim() !== oldName) {
            const parentPath = id.includes('/') ? id.substring(0, id.lastIndexOf('/')) : '';
            const newPath = parentPath ? `${parentPath}/${newName.trim()}` : newName.trim();
            onRenameFolder(id, newPath);
        }
    }
  };

  const handleDelete = async () => {
    if (!contextMenu) return;
    const { type, id } = contextMenu;
    setContextMenu(null);

    if (type === 'worksheet') {
        const ws = worksheets.find(w => w.id === id);
        if (ws) {
             const confirmed = await dialog.confirm('정말로 이 학습지를 삭제하시겠습니까?', { destructive: true });
             if(confirmed) onDeleteWorksheet(ws.id);
        }
    } else { // folder
        if (id === '/') {
            toast.error('루트 폴더는 삭제할 수 없습니다.');
            return;
        }
        const confirmed = await dialog.confirm('정말로 이 폴더를 삭제하시겠습니까? 내부의 모든 문제도 휴지통으로 이동됩니다.', { destructive: true });
        if(confirmed) onDeleteFolder(id);
    }
  };
  
  const isRootExpanded = expandedPaths.has('folder-root');
  const hasRootNotification = newContentFolders.size > 0 && !isRootExpanded;

  return (
    <nav style={style} className="bg-[var(--color-bg-glass)] border-r border-[var(--color-border-glass)] backdrop-blur-md p-2 overflow-y-auto overflow-x-hidden flex-shrink-0 flex flex-col">
      <div className="space-y-1 flex-grow">
        <div className="flex items-center justify-between">
            <div className="flex-grow">
                <TreeItem
                    isCollapsed={isCollapsed}
                    path="home"
                    label="Home"
                    icon={<HomeIcon className="h-full w-full" />}
                    isSelected={currentSelection?.type === 'suneung-home'}
                    onClick={() => onSelect({ type: 'suneung-home', id: 'home' })}
                    level={0}
                    isBold
                />
            </div>
             <Tooltip text={isCollapsed ? "펼치기" : "접기"}>
                <button onClick={onToggleCollapse} className="p-2 rounded-md hover:bg-white/10 text-[var(--color-text-secondary)] flex-shrink-0">
                    <CollapseIcon className="h-6 w-6" isCollapsed={isCollapsed} />
                </button>
            </Tooltip>
        </div>
        
        <div>
          <TreeItem
            isCollapsed={isCollapsed}
            path="/"
            label="문제 라이브러리"
            icon={expandedPaths.has('folder-root') ? <FolderOpenIcon className="h-full w-full"/> : <FolderIcon className="h-full w-full"/>}
            isSelected={currentSelection?.type === 'folder' && currentSelection.id === '/'}
            onClick={() => onSelect({ type: 'folder', id: '/' })}
            onContextMenu={(e) => handleContextMenu(e, 'folder', '/')}
            onToggleExpand={(e) => { e.stopPropagation(); toggleExpand('folder-root'); }}
            hasChildren
            isExpanded={expandedPaths.has('folder-root')}
            level={0}
            isBold
            count={baseProblems.length + problemHistory.length}
            hasNotification={hasRootNotification}
          />
          {!isCollapsed && expandedPaths.has('folder-root') && (
            <FolderTreeView
                isCollapsed={isCollapsed}
                nodes={folderTree}
                level={0}
                currentSelection={currentSelection}
                onSelect={onSelect}
                onContextMenu={(e, path) => handleContextMenu(e, 'folder', path)}
                expandedPaths={expandedPaths}
                toggleExpand={toggleExpand}
                newContentFolders={newContentFolders}
            />
          )}
        </div>
        
        <div>
           <TreeItem
            isCollapsed={isCollapsed}
            path="worksheets"
            label="학습지"
            icon={expandedPaths.has('worksheets') ? <FolderOpenIcon className="h-full w-full"/> : <FolderIcon className="h-full w-full"/>}
            isSelected={false}
            onClick={() => toggleExpand('worksheets')}
            onToggleExpand={() => toggleExpand('worksheets')}
            hasChildren
            isExpanded={expandedPaths.has('worksheets')}
            level={0}
            isBold
            count={worksheets.length}
          />
          {!isCollapsed && expandedPaths.has('worksheets') && (
            <div className="mt-1 space-y-1">
              {worksheets.map((ws) => (
                <TreeItem
                  key={ws.id}
                  isCollapsed={isCollapsed}
                  path={ws.id}
                  label={ws.name}
                  icon={<WorksheetIcon className="h-full w-full" />}
                  isSelected={currentSelection?.type === 'worksheet' && currentSelection.id === ws.id}
                  onClick={() => onSelect({ type: 'worksheet', id: ws.id })}
                  onContextMenu={(e) => handleContextMenu(e, 'worksheet', ws.id)}
                  level={1}
                />
              ))}
            </div>
          )}
        </div>

        <TreeItem
          isCollapsed={isCollapsed}
          path="jobs"
          label="작업 관리자"
          icon={<JobsIcon className="h-full w-full" />}
          count={activeJobsCount}
          isSelected={currentSelection?.type === 'jobs'}
          onClick={() => onSelect({ type: 'jobs', id: 'all' })}
          level={0}
          isBold
        />

        <TreeItem
          isCollapsed={isCollapsed}
          path="trash"
          label="휴지통"
          icon={<TrashIcon className="h-full w-full" />}
          count={trashedProblems.length}
          isSelected={currentSelection?.type === 'trash'}
          onClick={() => onSelect({ type: 'trash', id: 'all' })}
          level={0}
          isBold
        />
      </div>
       {contextMenu && (
        <div ref={menuRef} className="fixed w-40 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md shadow-lg z-50 text-sm" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <button onClick={handleRename} className="block w-full text-left px-3 py-2 hover:bg-white/10">이름 바꾸기</button>
          <button onClick={handleDelete} className="block w-full text-left px-3 py-2 text-rose-400 hover:bg-rose-500/20">삭제</button>
        </div>
      )}
    </nav>
  );
};

export default NavigationPane;
